<template>
  <WdsStack direction-column gap-lg>
    <BaseSection>
      <BaseSectionHeader :header-text="$t('Location')" />

      <BaseSectionBody>
        <WdsAlert info-subtle icon bordered class="tw-mb-1" data-test="location-legal-copy">
          {{ $t('The booking contract and invoices will use the address of your company') }}
        </WdsAlert>

        <BaseFieldGroup
          :label="$t('Work location')"
          :helper-text="$t('Where can this job be worked from?')"
        >
          <div class="tw-w-full">
            <WdsSelect
              v-model="selectedLocationPreference"
              :list="locationPreferenceOptions"
              :error="getErrorMessage(validationStore, 'locationPreference')"
              label=" "
              data-test="job-location-preference-dropdown"
              name="job-location-preference"
            />
          </div>
        </BaseFieldGroup>

        <div v-if="store.form.locationPreference !== LocationPreference.RemoteOnly">
          <BaseFieldGroup
            :label="$t('Address')"
            :helper-text="$t('Where the work will take place')"
          >
            <div class="tw-w-full">
              <WdsStack direction-column gap-md>
                <WdsInput
                  v-model="store.form.address"
                  :label="$t('Street')"
                  :placeholder="$t('Street name')"
                  data-test="job-location-address"
                  name="location_address"
                  :error="getErrorMessage(validationStore, 'address')"
                />

                <WdsStack direction-row gap-md>
                  <div
                    :class="{
                      'tw-w-1/2': countryHasPostcode,
                      'tw-w-full': !countryHasPostcode,
                    }"
                  >
                    <WdsInput
                      v-model="store.form.city"
                      :label="$t('City')"
                      data-test="job-location-city"
                      name="location_city"
                      :error="getErrorMessage(validationStore, 'city')"
                      class="tw-w-1/2"
                    />
                  </div>

                  <div v-if="countryHasPostcode" class="tw-w-1/2">
                    <WdsInput
                      v-model="store.form.postCode"
                      :label="postCodeLabel"
                      data-test="job-location-post-code"
                      name="location_zipcode"
                      :error="getErrorMessage(validationStore, 'postCode')"
                      class="tw-w-1/2"
                    />
                  </div>
                </WdsStack>

                <WdsStack direction-row gap-md>
                  <WdsSelect
                    v-model="selectedCountry"
                    :list="getCountries"
                    :label="$t('Country')"
                    :placeholder="$t('Select a country')"
                    searchable
                    :error="getErrorMessage(validationStore, 'country')"
                    data-test="job-location-country-dropdown"
                    name="location_country"
                    class="tw-w-1/2"
                  />

                  <WdsSelect
                    v-if="countryHasStates"
                    v-model="selectedState"
                    :list="getStates"
                    :label="stateLabel"
                    :placeholder="statePlaceholder"
                    searchable
                    :error="getErrorMessage(validationStore, 'state')"
                    data-test="job-location-state-dropdown"
                    name="location_state"
                    class="tw-w-1/2"
                  />
                </WdsStack>
              </WdsStack>
            </div>
          </BaseFieldGroup>
        </div>
      </BaseSectionBody>
    </BaseSection>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { WdsAlert, WdsStack, WdsSelect, WdsInput } from '@worksome/wds-components'

import { LocationPreference } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import { useLocation } from '@/composables/useLocation'

import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'

const store = useJobFormStore()
const validationStore = useValidationStore()

const {
  getCountries,
  getStates,
  setSelectedCountry,
  setSelectedState,
  postCodeLabel,
  stateLabel,
  statePlaceholder,
  countryHasStates,
  countryHasPostcode,
} = useLocation()

const locationPreferenceOptions = [
  { value: LocationPreference.OnsiteOnly, label: $t('On site') },
  { value: LocationPreference.OnsiteSome, label: $t('Hybrid (some days in-person)') },
  { value: LocationPreference.RemoteOnly, label: $t('Fully Remote') },
]

const selectedCountry = computed({
  get: () => ({ value: store.form.country?.toUpperCase() ?? '' }),
  set: (preference) => {
    store.form.country = preference.value
  },
})

const selectedState = computed({
  get: () => ({ value: store.form.state?.toUpperCase() ?? '' }),
  set: (preference) => {
    store.form.state = preference.value
  },
})

const selectedLocationPreference = computed({
  get: () => ({ value: store.form.locationPreference }),
  set: (preference) => {
    store.form.locationPreference = preference.value
  },
})

watch(
  () => store.form.country,
  (country) => {
    setSelectedCountry(country?.toUpperCase() ?? '')
  },
)

watch(
  () => store.form.state,
  (state) => {
    setSelectedState(state?.toUpperCase() ?? '')
  },
)
</script>
